/**
 * Password validation regex patterns
 * Based on MSD365 Commerce requirements + Venchi specific rules
 */

import { HAS_UPPERCASE_REGEX, HAS_LOWERCASE_REGEX, HAS_NUMBER_REGEX, HAS_SPECIAL_CHAR_REGEX, CURRENCY_SYMBOLS_REGEX } from './common';

// Base MSD365 Commerce password regex (at least 8 chars, 1 upper, 1 lower, 1 number or special)
export const MSD365_PASSWORD_BASE_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;

// Extended password requirements
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 16;

// Individual validation components
export const passwordValidationComponents = {
    hasUppercase: HAS_UPPERCASE_REGEX,
    hasLowercase: HAS_LOWERCASE_REGEX,
    hasNumber: HAS_NUMBER_REGEX,
    hasSpecialChar: HAS_SPECIAL_CHAR_REGEX,
    noCurrencySymbols: (value: string) => !CURRENCY_SYMBOLS_REGEX.test(value),
    validLength: (value: string) => value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH
};

/**
 * Comprehensive password validation function
 */
export function validatePassword(
    password: string
): {
    isValid: boolean;
    errors: string[];
} {
    const errors: string[] = [];

    if (!passwordValidationComponents.validLength(password)) {
        errors.push(`Password must be between ${PASSWORD_MIN_LENGTH} and ${PASSWORD_MAX_LENGTH} characters`);
    }

    if (!passwordValidationComponents.hasUppercase.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
    }

    if (!passwordValidationComponents.hasLowercase.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
    }

    if (!passwordValidationComponents.hasNumber.test(password)) {
        errors.push('Password must contain at least one number');
    }

    if (!passwordValidationComponents.hasSpecialChar.test(password)) {
        errors.push('Password must contain at least one special character');
    }

    if (!passwordValidationComponents.noCurrencySymbols(password)) {
        errors.push('Password cannot contain currency symbols (€, $, £, etc.)');
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}
